import { t } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import type { ChatPart } from 'features/timeSeries/agentTab/chatStream'
import React from 'react'
import styled from 'styled-components'

import { shortTag } from './tagLabel'

/**
 * The build trace: tags land as chips, then the KPI formula, then the fit's
 * settings and numbers — each the moment the agent does it, so a build is
 * watched rather than waited on.
 */

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-left: 2px solid ${({ theme }) => theme.forecast.primary};
  border-radius: 0.375rem;
  padding: 0.5rem 0.625rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
`

const Chip = styled(Text)`
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 999px;
  padding: 0.0625rem 0.5rem;
  white-space: nowrap;
`

const Stats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem 1rem;
  font-variant-numeric: tabular-nums;
`

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text as='div' variant='caption' color='muted'>
    {children}
  </Text>
)

const fmt = (value: unknown): string =>
  typeof value === 'number'
    ? String(Math.round(value * 1000) / 1000)
    : String(value ?? '—')

export const RolesCard: React.FC<{
  part: Extract<ChatPart, { kind: 'roles' }>
}> = ({ part }) => {
  const { t } = useLingui()
  const dataset = part.dataset
  return (
    <Card>
      <Label>{t`tags picked · dataset “${dataset}”`}</Label>
      <Chips>
        {Object.entries(part.roles).map(([role, tag]) => (
          <Chip key={role} variant='code' size='xxs' title={tag}>
            {role} ← {shortTag(tag)}
          </Chip>
        ))}
      </Chips>
    </Card>
  )
}

export const KpiCard: React.FC<{
  part: Extract<ChatPart, { kind: 'kpi' }>
}> = ({ part }) => {
  const { t } = useLingui()
  const kpi = part.kpi
  return (
    <Card>
      <Label>{t`physics KPI · ${kpi}`}</Label>
      <Text as='code' variant='code' size='xs'>
        {part.expr}
      </Text>
    </Card>
  )
}

export const FitCard: React.FC<{
  part: Extract<ChatPart, { kind: 'fit' }>
}> = ({ part }) => {
  const { t } = useLingui()
  const stats = part.stats
  const baseline = stats.baseline_window as unknown[] | undefined
  const baselineStart = fmt(baseline?.[0])
  const baselineEnd = fmt(baseline?.[1])
  const gate = stats.gate_expr ? String(stats.gate_expr) : t`none`
  const drivers = Array.isArray(stats.drivers) ? stats.drivers.join(', ') : '—'
  const baselineMedian = fmt(stats.baseline_median)
  const last30Actual = fmt(stats.last30_actual)
  const last30Expected = fmt(stats.last30_expected)
  const last30Gap = fmt(stats.last30_gap)
  const last30Sigma = fmt(stats.last30_sigma)
  const baselineRows = fmt(stats.baseline_rows)
  const dataThrough = fmt(stats.data_through)
  return (
    <Card>
      <Label>
        {t`field fit · baseline ${baselineStart} → ${baselineEnd} · gate ${gate} · drivers ${drivers}`}
      </Label>
      <Stats>
        <Text variant='caption'>
          <Trans>
            baseline <b>{baselineMedian}</b>
          </Trans>
        </Text>
        <Text variant='caption'>
          <Trans>
            last 30d <b>{last30Actual}</b> vs expected <b>{last30Expected}</b>
          </Trans>
        </Text>
        <Text variant='caption'>
          <Trans>
            gap <b>{last30Gap}</b> ({last30Sigma}σ)
          </Trans>
        </Text>
        <Text variant='caption'>
          {t`${baselineRows} baseline rows · data through ${dataThrough}`}
        </Text>
      </Stats>
      <Label>
        {t`drawn on the chart · save it in the model card below to alert or backtest`}
      </Label>
    </Card>
  )
}

export const SavedCard: React.FC<{
  part: Extract<ChatPart, { kind: 'saved' }>
}> = ({ part }) => (
  <Card>
    <Label>
      <Trans>model saved</Trans>
    </Label>
    <Text variant='caption'>
      <b>{part.name}</b> · <code>{part.entryId}</code>
    </Text>
  </Card>
)

/** What the agent changed about the view, for someone reading it back later. */
const focusSummary = (part: Extract<ChatPart, { kind: 'focus' }>): string[] => {
  const done: string[] = []
  if (part.start && part.end) done.push(`${part.start} → ${part.end}`)
  const shown = part.show.length
  const hidden = part.hide.length
  if (shown) done.push(t`showed ${shown}`)
  if (hidden) done.push(t`hid ${hidden}`)
  const axis = part.axis
  if (axis) done.push(t`${axis} axis`)
  if (part.band) {
    const bandStart = part.band.start
    const bandEnd = part.band.end
    done.push(t`banded ${bandStart} → ${bandEnd}`)
  }
  return done
}

export const FocusCard: React.FC<{
  part: Extract<ChatPart, { kind: 'focus' }>
}> = ({ part }) => {
  const { t } = useLingui()
  const summary = focusSummary(part)
  if (!summary.length && !part.note) return null
  const changes = summary.join(' · ') || t`unchanged`
  return (
    <Card>
      <Label>{t`chart · ${changes}`}</Label>
      {part.note && <Text variant='caption'>{part.note}</Text>}
    </Card>
  )
}

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
`

const Remove = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  cursor: pointer;
  font: inherit;
  text-decoration: underline;

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
  }
`

const penDescription = (
  part: Extract<ChatPart, { kind: 'pen' }>
): string | null => {
  if (!part.spec) return null
  if (part.spec.kind === 'average') {
    const hours = part.spec.hours
    const tag = shortTag(part.spec.tag)
    return t`${hours}h rolling average of ${tag}`
  }
  return part.spec.expr
}

export const PenCard: React.FC<{
  part: Extract<ChatPart, { kind: 'pen' }>
  /** True while the pen is still on the chart, which is what makes it removable. */
  onChart: boolean
  onRemove: () => void
}> = ({ part, onChart, onRemove }) => {
  const { t } = useLingui()
  const description = penDescription(part)
  return (
    <Card>
      <Row>
        <Label>{part.op === 'remove' ? t`pen removed` : t`pen added`}</Label>
        {part.op === 'add' && onChart && (
          <Remove type='button' onClick={onRemove}>
            <Text variant='caption' color='tertiary'>
              {t`remove`}
            </Text>
          </Remove>
        )}
      </Row>
      <Text variant='caption'>
        <b>{part.name}</b>
        {description && (
          <>
            {' · '}
            <Text variant='caption' color='muted'>
              {description}
            </Text>
          </>
        )}
      </Text>
    </Card>
  )
}
